import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Quiz as QuizIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Psychology as PsychologyIcon,
  ExpandMore as ExpandMoreIcon,
  VideoLibrary as VideoIcon,
  MenuBook as BookIcon,
  Description as DescriptionIcon,
  AccessTime as TimeIcon,
  Person as TeacherIcon,
  LibraryBooks as LibraryIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  Star as StarIcon,
  PictureAsPdf as PdfIcon,
  PlayCircle as PlayCircleIcon,
  Close as CloseIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Send as SendIcon,
  Help as HelpIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import { resolveCurriculumChain } from '../hooks/useDeliverablesPolling.ts';
import type { StudentDeliverable } from '../types/curriculum.types.ts';

// Student data with 3 different students
const STUDENTS_DATA = {
  student1: {
    id: 'student1',
    name: 'Aarav Sharma',
    class: '6',
    rollNo: '24',
    performance: {
      weekly: [{ week: 'Week 1', score: 85 }, { week: 'Week 2', score: 92 }, { week: 'Week 3', score: 78 }, { week: 'Week 4', score: 88 }],
      subjects: [
        { subject: 'Mathematics', score: 86, trend: '+5%', teacherFeedback: 'Good progress, keep practicing algebra' },
        { subject: 'Science', score: 92, trend: '+8%', teacherFeedback: 'Excellent understanding of concepts' },
        { subject: 'English', score: 84, trend: '+3%', teacherFeedback: 'Improve vocabulary' },
      ],
      conceptMastery: [
        { concept: 'Number Systems', mastery: 90, weakAreas: ['Large Numbers'] },
        { concept: 'Fractions', mastery: 75, weakAreas: ['Fraction Addition', 'Mixed Fractions'] },
        { concept: 'Geometry', mastery: 85, weakAreas: ['Angles'] },
        { concept: 'Algebra', mastery: 68, weakAreas: ['Expressions', 'Equations'] },
      ],
    },
    studyPlan: {
      weeklyPlan: [
        {
          week: 1,
          topic: 'Number Systems',
          description: 'Understanding large numbers, place value, and number comparisons',
          duration: '5 hours',
          subtopics: ['Indian and International Number Systems', 'Place Value and Face Value', 'Comparing and Ordering Numbers', 'Rounding Numbers'],
          videos: [
            { name: 'Introduction to Large Numbers', duration: '12 min', platform: 'YouTube' },
            { name: 'Place Value Explained', duration: '8 min', platform: 'YouTube' },
          ],
          books: [
            { name: 'NCERT Mathematics - Chapter 1', pages: '12-25', author: 'NCERT' },
            { name: 'RD Sharma - Number Systems', pages: '5-18', author: 'RD Sharma' },
          ],
          pdfs: [
            { name: 'Number Systems Notes', pages: 15, size: '2.4 MB' },
            { name: 'Practice Questions Set', pages: 25, size: '3.1 MB' },
          ],
          practice: [
            { name: 'Number System Worksheet', problems: 25, difficulty: 'Easy', timeEstimate: '30 min' },
            { name: 'Place Value Practice', problems: 15, difficulty: 'Medium', timeEstimate: '25 min' },
          ],
          teacherExamples: [
            'Compare numbers: 5,43,210 and 5,34,210',
            'Find place value of 6 in 56,78,910',
            'Write 45,67,890 in words',
          ],
          questionsToAskTeacher: [
            'How to compare numbers with different digits?',
            'What is the difference between Indian and International system?',
          ],
          status: 'completed',
          score: 85,
        },
        {
          week: 2,
          topic: 'Fractions & Decimals',
          description: 'Operations on fractions, decimal numbers, and their applications',
          duration: '6 hours',
          subtopics: ['Types of Fractions', 'Addition and Subtraction of Fractions', 'Multiplication and Division of Fractions', 'Decimals and their Operations'],
          videos: [
            { name: 'Understanding Fractions', duration: '15 min', platform: 'YouTube' },
            { name: 'Decimal Operations', duration: '10 min', platform: 'Khan Academy' },
          ],
          books: [
            { name: 'NCERT Mathematics - Chapter 2', pages: '26-45', author: 'NCERT' },
            { name: 'RS Aggarwal - Fractions', pages: '20-35', author: 'RS Aggarwal' },
          ],
          pdfs: [
            { name: 'Fractions Study Guide', pages: 20, size: '2.8 MB' },
            { name: 'Decimal Operations Handbook', pages: 18, size: '2.2 MB' },
          ],
          practice: [
            { name: 'Fraction Operations Worksheet', problems: 30, difficulty: 'Medium', timeEstimate: '40 min' },
            { name: 'Decimal Practice Set', problems: 20, difficulty: 'Medium', timeEstimate: '35 min' },
          ],
          teacherExamples: [
            'Add 2/3 + 3/4',
            'Convert 0.75 to fraction',
            'Multiply 3/4 × 2/5',
          ],
          questionsToAskTeacher: [
            'How to add unlike fractions easily?',
            'What is the trick for decimal multiplication?',
          ],
          status: 'completed',
          score: 92,
        },
        {
          week: 3,
          topic: 'Algebra Basics',
          description: 'Introduction to variables, expressions, and simple equations',
          duration: '6 hours',
          subtopics: ['Variables and Constants', 'Algebraic Expressions', 'Like and Unlike Terms', 'Simple Linear Equations'],
          videos: [
            { name: 'What is Algebra?', duration: '10 min', platform: 'YouTube' },
            { name: 'Forming Algebraic Expressions', duration: '14 min', platform: 'Khan Academy' },
            { name: 'Solving Equations', duration: '18 min', platform: 'YouTube' },
          ],
          books: [
            { name: 'NCERT Mathematics - Chapter 7', pages: '100-120', author: 'NCERT' },
            { name: 'Lakhmir Singh - Algebra', pages: '40-55', author: 'Lakhmir Singh' },
          ],
          pdfs: [
            { name: 'Algebra Formula Sheet', pages: 4, size: '0.8 MB' },
            { name: 'Expression Simplification Guide', pages: 25, size: '3.2 MB' },
          ],
          practice: [
            { name: 'Expression Formation Worksheet', problems: 25, difficulty: 'Easy', timeEstimate: '30 min' },
            { name: 'Equation Solving Practice', problems: 20, difficulty: 'Medium', timeEstimate: '40 min' },
          ],
          teacherExamples: [
            'Simplify: 3x + 2x - 5x',
            'Solve: 2x + 5 = 15',
            'Find value: If x=3, then 2x² + 3x - 5',
          ],
          questionsToAskTeacher: [
            'How to identify like terms?',
            'What is the rule for solving equations?',
            'How to handle negative coefficients?',
          ],
          status: 'in-progress',
          score: null,
        },
        {
          week: 4,
          topic: 'Geometry',
          description: 'Basic geometric shapes, angles, and their properties',
          duration: '5 hours',
          subtopics: ['Lines and Angles', 'Triangles and Quadrilaterals', 'Circles and their Parts', 'Symmetry and Transformations'],
          videos: [
            { name: 'Basic Geometry Concepts', duration: '15 min', platform: 'YouTube' },
            { name: 'Types of Angles', duration: '8 min', platform: 'Khan Academy' },
            { name: 'Triangles Explained', duration: '12 min', platform: 'YouTube' },
          ],
          books: [
            { name: 'NCERT Mathematics - Chapter 4', pages: '50-70', author: 'NCERT' },
            { name: 'RD Sharma - Geometry', pages: '30-45', author: 'RD Sharma' },
          ],
          pdfs: [
            { name: 'Geometry Handbook', pages: 35, size: '4.2 MB' },
            { name: 'Angle Properties Guide', pages: 12, size: '1.5 MB' },
          ],
          practice: [
            { name: 'Angles Worksheet', problems: 25, difficulty: 'Easy', timeEstimate: '35 min' },
            { name: 'Triangles Practice', problems: 20, difficulty: 'Medium', timeEstimate: '45 min' },
          ],
          teacherExamples: [
            'Find the third angle of triangle if two angles are 50° and 60°',
            'Properties of parallelogram',
            'Find area of rectangle with length 12cm and breadth 8cm',
          ],
          questionsToAskTeacher: [
            'How to identify different types of angles?',
            'What is the Pythagoras theorem?',
            'How to calculate area of circle?',
          ],
          status: 'pending',
          score: null,
        },
      ],
      teacherTips: [
        'Practice daily 10 algebra problems',
        'Watch concept videos before solving',
        'Make formula flashcards',
        'Solve previous year worksheets',
      ],
    },
  },
  student2: {
    id: 'student2',
    name: 'Priya Patel',
    class: '7',
    rollNo: '12',
    performance: {
      weekly: [{ week: 'Week 1', score: 95 }, { week: 'Week 2', score: 88 }, { week: 'Week 3', score: 94 }, { week: 'Week 4', score: 96 }],
      subjects: [
        { subject: 'Mathematics', score: 93, trend: '+4%', teacherFeedback: 'Outstanding performance' },
        { subject: 'Science', score: 88, trend: '+2%', teacherFeedback: 'Good, need more lab work' },
        { subject: 'English', score: 91, trend: '+6%', teacherFeedback: 'Excellent writing skills' },
      ],
      conceptMastery: [
        { concept: 'Algebra', mastery: 95, weakAreas: [] },
        { concept: 'Geometry', mastery: 82, weakAreas: ['Coordinate Geometry'] },
        { concept: 'Fractions', mastery: 88, weakAreas: [] },
      ],
    },
    studyPlan: {
      weeklyPlan: [
        {
          week: 1,
          topic: 'Linear Equations',
          description: 'Solving linear equations in one variable',
          duration: '5 hours',
          subtopics: ['Simple Equations', 'Equations with Variables on Both Sides', 'Cross Multiplication', 'Word Problems'],
          videos: [{ name: 'Linear Equations Tutorial', duration: '15 min', platform: 'YouTube' }],
          books: [{ name: 'NCERT Mathematics', pages: '80-95', author: 'NCERT' }],
          pdfs: [{ name: 'Linear Equations Notes', pages: 12, size: '1.5 MB' }],
          practice: [{ name: 'Equation Practice', problems: 25, difficulty: 'Medium', timeEstimate: '35 min' }],
          teacherExamples: ['Solve: 3x + 7 = 25', 'Solve: 2(x + 3) = 12'],
          questionsToAskTeacher: ['How to solve equations with variables on both sides?'],
          status: 'completed',
          score: 95,
        },
        {
          week: 2,
          topic: 'Mensuration',
          description: 'Area and perimeter of different shapes',
          duration: '6 hours',
          subtopics: ['Area of Rectangle', 'Area of Circle', 'Surface Area', 'Volume'],
          videos: [{ name: 'Mensuration Basics', duration: '20 min', platform: 'YouTube' }],
          books: [{ name: 'RD Sharma - Mensuration', pages: '50-70', author: 'RD Sharma' }],
          pdfs: [{ name: 'Formula Sheet', pages: 6, size: '0.8 MB' }],
          practice: [{ name: 'Area & Perimeter Practice', problems: 30, difficulty: 'Medium', timeEstimate: '45 min' }],
          teacherExamples: ['Find area of circle with radius 7cm', 'Find volume of cube with edge 5cm'],
          questionsToAskTeacher: ['Formula for surface area of cylinder?'],
          status: 'in-progress',
          score: null,
        },
      ],
      teacherTips: ['Focus on word problems', 'Practice mensuration formulas daily'],
    },
  },
  student3: {
    id: 'student3',
    name: 'Rohan Verma',
    class: '8',
    rollNo: '8',
    performance: {
      weekly: [{ week: 'Week 1', score: 72 }, { week: 'Week 2', score: 78 }, { week: 'Week 3', score: 82 }, { week: 'Week 4', score: 85 }],
      subjects: [
        { subject: 'Mathematics', score: 78, trend: '+10%', teacherFeedback: 'Showing great improvement' },
        { subject: 'Science', score: 75, trend: '+5%', teacherFeedback: 'Need more practice' },
        { subject: 'English', score: 80, trend: '+4%', teacherFeedback: 'Good progress' },
      ],
      conceptMastery: [
        { concept: 'Linear Equations', mastery: 72, weakAreas: ['Word Problems'] },
        { concept: 'Quadrilaterals', mastery: 68, weakAreas: ['Properties'] },
        { concept: 'Rational Numbers', mastery: 80, weakAreas: [] },
      ],
    },
    studyPlan: {
      weeklyPlan: [
        {
          week: 1,
          topic: 'Rational Numbers',
          description: 'Understanding rational numbers and their operations',
          duration: '5 hours',
          subtopics: ['Properties of Rational Numbers', 'Addition & Subtraction', 'Multiplication & Division', 'Rational Numbers on Number Line'],
          videos: [{ name: 'Rational Numbers Explained', duration: '18 min', platform: 'YouTube' }],
          books: [{ name: 'NCERT Mathematics - Chapter 1', pages: '1-20', author: 'NCERT' }],
          pdfs: [{ name: 'Rational Numbers Guide', pages: 15, size: '2 MB' }],
          practice: [{ name: 'Rational Numbers Worksheet', problems: 25, difficulty: 'Medium', timeEstimate: '40 min' }],
          teacherExamples: ['Add 2/3 + (-4/5)', 'Multiply -3/4 × 2/7'],
          questionsToAskTeacher: ['How to compare rational numbers?', 'Properties of rational numbers?'],
          status: 'completed',
          score: 78,
        },
        {
          week: 2,
          topic: 'Understanding Quadrilaterals',
          description: 'Properties of different quadrilaterals',
          duration: '6 hours',
          subtopics: ['Types of Quadrilaterals', 'Properties of Parallelogram', 'Properties of Rectangle, Square, Rhombus', 'Angle Sum Property'],
          videos: [{ name: 'Quadrilaterals Explained', duration: '22 min', platform: 'YouTube' }],
          books: [{ name: 'NCERT Mathematics - Chapter 3', pages: '40-60', author: 'NCERT' }],
          pdfs: [{ name: 'Quadrilaterals Properties', pages: 10, size: '1.2 MB' }],
          practice: [{ name: 'Geometry Practice', problems: 20, difficulty: 'Medium', timeEstimate: '35 min' }],
          teacherExamples: ['Find angles of parallelogram', 'Properties of rhombus'],
          questionsToAskTeacher: ['How to identify different quadrilaterals?', 'Angle sum property proof?'],
          status: 'in-progress',
          score: null,
        },
      ],
      teacherTips: ['Practice rational number operations daily', 'Draw diagrams for geometry'],
    },
  },
};

// Video Player Dialog Component
const VideoPlayerDialog = ({ open, onClose, videoTitle }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h6">🎬 {videoTitle}</Typography>
      <IconButton onClick={onClose}><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent>
      <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', bgcolor: '#000', borderRadius: 1 }}>
        <iframe
          src="https://www.youtube.com/embed/dQw4w9WgXcQ"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          frameBorder="0"
          allowFullScreen
          title={videoTitle}
        />
      </Box>
    </DialogContent>
  </Dialog>
);

// PDF Viewer Dialog
const PdfViewerDialog = ({ open, onClose, pdfTitle }) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h6">📄 {pdfTitle}</Typography>
      <IconButton onClick={onClose}><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent>
      <Box sx={{ height: '60vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, flexDirection: 'column', gap: 2 }}>
        <PdfIcon sx={{ fontSize: 80, color: '#ef4444' }} />
        <Typography variant="body1" color="text.secondary">PDF Preview - {pdfTitle}</Typography>
        <Typography variant="caption" color="text.secondary">Click download button to view full PDF</Typography>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button variant="contained" startIcon={<DownloadIcon />}>Download PDF</Button>
    </DialogActions>
  </Dialog>
);

// Mock Test Dialog
const MockTestDialog = ({ open, onClose, testTitle }) => {
  const aiActions = AiActions();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [evalResult, setEvalResult] = useState('');
  
  const mockQuestions = [
    { id: 1, text: 'What is the value of x in 2x + 5 = 15?', options: ['5', '10', '7', '8'], correct: '5' },
    { id: 2, text: 'Simplify: 3x + 2x - 5x', options: ['0', 'x', '2x', '10x'], correct: '0' },
    { id: 3, text: 'If x = 3, find 2x² + 3x - 5', options: ['22', '18', '25', '20'], correct: '22' },
    { id: 4, text: 'What is the value of 5²?', options: ['10', '15', '20', '25'], correct: '25' },
    { id: 5, text: 'Solve: x/2 = 10', options: ['5', '10', '15', '20'], correct: '20' },
  ];

  const handleAnswer = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleSubmit = async () => {
    setEvaluating(true);
    let totalScore = 0;
    let maxTotal = 0;

    for (const q of mockQuestions) {
      const studentAnswer = answers[q.id] ?? '';
      try {
        const result = await aiActions.evaluateAnswer({
          question_text: q.text,
          student_answer: String(studentAnswer),
          reference_answer: q.correct,
          topic: testTitle,
        });
        totalScore += result.score ?? 0;
        maxTotal += result.max_marks ?? 5;
      } catch {
        if (studentAnswer === q.correct) {
          totalScore += 5;
          maxTotal += 5;
        } else {
          maxTotal += 5;
        }
      }
    }

    setEvalResult(`You scored ${totalScore}/${maxTotal} on ${testTitle}!`);
    setSubmitted(true);
    setEvaluating(false);
    setTimeout(() => {
      onClose();
      setSubmitted(false);
      setAnswers({});
      setCurrentQuestion(0);
      setEvalResult('');
    }, 2500);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {testTitle} - Question {currentQuestion + 1}/{mockQuestions.length}
      </DialogTitle>
      <DialogContent>
        {!submitted ? (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>{mockQuestions[currentQuestion].text}</Typography>
            <Stack spacing={1.5}>
              {mockQuestions[currentQuestion].options.map((opt, idx) => (
                <Button
                  key={idx}
                  variant={answers[mockQuestions[currentQuestion].id] === opt ? 'contained' : 'outlined'}
                  onClick={() => handleAnswer(mockQuestions[currentQuestion].id, opt)}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1 }}
                >
                  {opt}
                </Button>
              ))}
            </Stack>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50' }} />
            <Typography variant="h6" sx={{ mt: 2 }}>{evalResult || 'Test Submitted!'}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {currentQuestion < mockQuestions.length - 1 ? (
          <Button variant="contained" onClick={() => setCurrentQuestion(currentQuestion + 1)}>Next</Button>
        ) : (
          <Button variant="contained" color="success" onClick={handleSubmit} disabled={evaluating}>
            {evaluating ? 'Evaluating...' : 'Submit Test'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Teacher Question Dialog
const TeacherQuestionDialog = ({ open, onClose, topic, questionsToAsk }) => {
  const [question, setQuestion] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (question.trim()) {
      setSubmitted(true);
      setTimeout(() => {
        onClose();
        setQuestion('');
        setSubmitted(false);
      }, 1500);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TeacherIcon sx={{ color: '#3b82f6' }} /> Ask Teacher about {topic}
      </DialogTitle>
      <DialogContent>
        {!submitted ? (
          <Box sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Suggested questions you can ask:
            </Typography>
            <Stack spacing={1} sx={{ mb: 3 }}>
              {questionsToAsk.map((q, idx) => (
                <Chip
                  key={idx}
                  label={q}
                  onClick={() => setQuestion(q)}
                  variant="outlined"
                  sx={{ justifyContent: 'flex-start', height: 'auto', py: 1, whiteSpace: 'normal', textAlign: 'left' }}
                />
              ))}
            </Stack>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Your Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Type your question here..."
            />
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SendIcon sx={{ fontSize: 48, color: '#4caf50' }} />
            <Typography variant="h6" sx={{ mt: 2 }}>Question Sent to Teacher!</Typography>
            <Typography variant="body2" color="text.secondary">Teacher will respond within 24 hours.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!question.trim()}>Send Question</Button>
      </DialogActions>
    </Dialog>
  );
};

export default function StudentDashboard() {
  const [selectedStudent, setSelectedStudent] = useState('student1');
  const [selectedView, setSelectedView] = useState('overview');
  const [expandedWeek, setExpandedWeek] = useState(1);
  const [videoOpen, setVideoOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [mockTestOpen, setMockTestOpen] = useState(false);
  const [questionOpen, setQuestionOpen] = useState(false);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [studentMenuAnchor, setStudentMenuAnchor] = useState(null);
  const [deliverables, setDeliverables] = useState<StudentDeliverable[]>([]);
  const aiActions = AiActions();

  useEffect(() => {
    const loadDeliverables = async () => {
      try {
        const maps = await aiActions.getKnowledgeList();
        if (maps?.length > 0) {
          const kmId = maps[0].id ?? maps[0]._id ?? '';
          const chain = await resolveCurriculumChain(kmId, aiActions);
          setDeliverables(chain.deliverables ?? []);
        }
      } catch {
        setDeliverables([]);
      }
    };
    loadDeliverables();
  }, []);

  const student = STUDENTS_DATA[selectedStudent];
  const currentWeekPlan = student.studyPlan.weeklyPlan.find(w => w.week === expandedWeek);

  // Calculate averages
  const avgScore = student.performance.weekly.reduce((sum, w) => sum + w.score, 0) / student.performance.weekly.length;
  const weeklyData = student.performance.weekly.map(w => w.score);
  const weeklyLabels = student.performance.weekly.map(w => w.week);

  const weeklyLineChart = {
    series: [{ name: 'Performance Score', data: weeklyData }],
    options: {
      chart: { type: 'line', height: 250, toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { curve: 'smooth', width: 3, colors: ['#8884d8'] },
      markers: { size: 6, colors: ['#8884d8'], strokeColors: '#fff', strokeWidth: 2 },
      xaxis: { categories: weeklyLabels, labels: { style: { fontSize: '11px' } } },
      yaxis: { title: { text: 'Score (%)' }, min: 0, max: 100, labels: { formatter: (val) => `${val}%` } },
      tooltip: { y: { formatter: (val) => `${val}%` } },
    },
  };

  const overallGaugeChart = {
    series: [Math.round(avgScore)],
    options: {
      chart: { type: 'radialBar', height: 280, toolbar: { show: false } },
      plotOptions: {
        radialBar: {
          startAngle: -135, endAngle: 135, hollow: { size: '65%' },
          track: { background: '#e2e8f0', strokeWidth: '97%' },
          dataLabels: { value: { fontSize: '30px', fontWeight: 700, formatter: (val) => `${val}%` } },
        },
      },
      colors: ['#8b5cf6'],
    },
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return { bg: '#22c55e20', color: '#16a34a', icon: <CheckCircleIcon /> };
      case 'in-progress': return { bg: '#3b82f620', color: '#2563eb', icon: <TrendingUpIcon /> };
      default: return { bg: '#f1f5f9', color: '#64748b', icon: <ScheduleIcon /> };
    }
  };

  const handleOpenVideo = (topic) => {
    setCurrentTopic(topic);
    setVideoOpen(true);
  };

  const handleOpenPdf = (topic) => {
    setCurrentTopic(topic);
    setPdfOpen(true);
  };

  const handleOpenMockTest = (topic) => {
    setCurrentTopic(topic);
    setMockTestOpen(true);
  };

  const handleAskTeacher = (topic, questions) => {
    setCurrentTopic({ topic, questions });
    setQuestionOpen(true);
  };

  const getStudentInitial = (name) => name.charAt(0);

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh', paddingLeft:{md:'200px',sm:'100%'} }}>
      {/* Header with Student Selector */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 50, height: 50, bgcolor: '#667eea' }}>{getStudentInitial(student.name)}</Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{student.name}</Typography>
            <Typography variant="caption" color="text.secondary">Class {student.class} • Roll No: {student.rollNo}</Typography>
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<SchoolIcon />} onClick={(e) => setStudentMenuAnchor(e.currentTarget)}>
          Switch Student
        </Button>
        <Menu anchorEl={studentMenuAnchor} open={Boolean(studentMenuAnchor)} onClose={() => setStudentMenuAnchor(null)}>
          {Object.entries(STUDENTS_DATA).map(([id, s]) => (
            <MenuItem key={id} onClick={() => { setSelectedStudent(id); setStudentMenuAnchor(null); setExpandedWeek(1); }}>
              <Avatar sx={{ width: 30, height: 30, mr: 1, bgcolor: '#667eea' }}>{s.name.charAt(0)}</Avatar>
              <Box><Typography variant="body2">{s.name}</Typography><Typography variant="caption" color="text.secondary">Class {s.class}</Typography></Box>
            </MenuItem>
          ))}
        </Menu>
      </Paper>

      {deliverables.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Your Study Materials (from API)</Typography>
          <Stack spacing={1}>
            {deliverables.slice(0, 5).map((d) => (
              <Alert key={d._id} severity="info" sx={{ fontSize: 12 }}>
                <Typography variant="subtitle2">{d.topic}</Typography>
                {d.student_notes && <Typography variant="caption" sx={{ display: 'block' }}>{d.student_notes}</Typography>}
                {d.homework && <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>Homework: {d.homework}</Typography>}
              </Alert>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Welcome Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5">Welcome back, {student.name}! 👋</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
              {student.performance.subjects[0]?.teacherFeedback || 'Keep up the good work!'}
            </Typography>
          </Box>
          <Chip icon={<TeacherIcon />} label="Teacher's Feedback Available" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
        </Box>
      </Paper>

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', p: 1, gap: 1 }}>
          {[
            { id: 'overview', label: '📊 Overview', icon: <TrendingUpIcon /> },
            { id: 'studyPlan', label: '📚 Study Plan', icon: <ScheduleIcon /> },
            { id: 'performance', label: '🎯 Performance', icon: <PsychologyIcon /> },
          ].map(tab => (
            <Button key={tab.id} variant={selectedView === tab.id ? 'contained' : 'outlined'} onClick={() => setSelectedView(tab.id)} startIcon={tab.icon} sx={{ flex: 1, py: 1 }}>
              {tab.label}
            </Button>
          ))}
        </Box>
      </Paper>

      {/* Overview View */}
      {selectedView === 'overview' && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}><Card><CardContent><Chart options={weeklyLineChart.options} series={weeklyLineChart.series} type="line" height={280} /></CardContent></Card></Grid>
          <Grid item xs={12} lg={6}><Card><CardContent><Chart options={overallGaugeChart.options} series={overallGaugeChart.series} type="radialBar" height={280} /></CardContent></Card></Grid>
          <Grid item xs={12}>
            <Card><CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>📚 Subject-wise Performance</Typography>
              {student.performance.subjects.map((subject, idx) => (
                <Box key={idx} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{subject.subject}</Typography>
                    <Typography variant="body2" color={subject.score >= 80 ? 'success.main' : 'warning.main'}>{subject.score}% {subject.trend}</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={subject.score} sx={{ height: 8, borderRadius: 4 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>📝 {subject.teacherFeedback}</Typography>
                </Box>
              ))}
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* Study Plan View with Week-wise Accordions */}
      {selectedView === 'studyPlan' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>📚 Personalized Study Plan</Typography>
                
                {/* Week Progress Bar */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    {student.studyPlan.weeklyPlan.map((week) => (
                      <Typography key={week.week} variant="caption" sx={{ fontSize: 11, fontWeight: 500, textAlign: 'center', flex: 1 }}>
                        Week {week.week}
                      </Typography>
                    ))}
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={student.studyPlan.weeklyPlan.filter(w => w.status === 'completed').length / student.studyPlan.weeklyPlan.length * 100} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                {/* Week-wise Accordions */}
                {student.studyPlan.weeklyPlan.map((week) => {
                  const statusStyle = getStatusColor(week.status);
                  return (
                    <Accordion 
                      key={week.week} 
                      expanded={expandedWeek === week.week} 
                      onChange={() => setExpandedWeek(expandedWeek === week.week ? null : week.week)}
                      sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, border: `1px solid ${statusStyle.color}20` }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: statusStyle.bg, borderRadius: '12px 12px 0 0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: statusStyle.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                            {statusStyle.icon}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14 }}>Week {week.week}: {week.topic}</Typography>
                            <Typography variant="caption" color="text.secondary">{week.duration} • {week.subtopics.length} subtopics</Typography>
                          </Box>
                          {week.score && <Chip label={`Score: ${week.score}%`} size="small" color="success" />}
                          <Chip 
                            label={week.status === 'completed' ? 'Completed' : week.status === 'in-progress' ? 'In Progress' : 'Pending'} 
                            size="small" 
                            sx={{ bgcolor: statusStyle.color, color: 'white' }} 
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{week.description}</Typography>

                        <Grid container spacing={2}>
                          {/* Subtopics */}
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><BookIcon sx={{ fontSize: 18 }} /> Subtopics to Cover</Typography>
                              <List dense>
                                {week.subtopics.map((st, idx) => (
                                  <ListItem key={idx} sx={{ py: 0.5 }}>
                                    <ListItemIcon><CheckCircleIcon sx={{ fontSize: 16, color: statusStyle.color }} /></ListItemIcon>
                                    <ListItemText primary={st} primaryTypographyProps={{ fontSize: 12 }} />
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          </Grid>

                          {/* Video Resources */}
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><VideoIcon sx={{ fontSize: 18 }} /> Video Resources</Typography>
                              {week.videos.map((video, idx) => (
                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>{video.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{video.platform} • {video.duration}</Typography>
                                  </Box>
                                  <Button size="small" variant="outlined" onClick={() => handleOpenVideo(week.topic)}>Watch</Button>
                                </Box>
                              ))}
                            </Paper>
                          </Grid>

                          {/* PDF Resources */}
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><PdfIcon sx={{ fontSize: 18 }} /> PDF Resources</Typography>
                              {week.pdfs.map((pdf, idx) => (
                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>{pdf.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{pdf.pages} pages • {pdf.size}</Typography>
                                  </Box>
                                  <Button size="small" variant="outlined" onClick={() => handleOpenPdf(week.topic)}>View</Button>
                                </Box>
                              ))}
                            </Paper>
                          </Grid>

                          {/* Practice Worksheets */}
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><AssignmentIcon sx={{ fontSize: 18 }} /> Practice Worksheets</Typography>
                              {week.practice.map((practice, idx) => (
                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>{practice.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{practice.problems} problems • {practice.difficulty}</Typography>
                                    <Typography variant="caption" display="block" color="text.secondary">⏱️ {practice.timeEstimate}</Typography>
                                  </Box>
                                  <Button size="small" variant="outlined">Practice</Button>
                                </Box>
                              ))}
                            </Paper>
                          </Grid>

                          {/* Teacher Examples */}
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fef3c7' }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><TeacherIcon sx={{ fontSize: 18 }} /> Examples from Teacher</Typography>
                              <List dense>
                                {week.teacherExamples.map((example, idx) => (
                                  <ListItem key={idx} sx={{ py: 0.5 }}>
                                    <ListItemIcon><StarIcon sx={{ fontSize: 14, color: '#f59e0b' }} /></ListItemIcon>
                                    <ListItemText primary={example} primaryTypographyProps={{ fontSize: 11 }} />
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          </Grid>

                          {/* Questions to Ask Teacher */}
                          <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e0f2fe' }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><QuestionAnswerIcon sx={{ fontSize: 18 }} /> Questions to Ask Teacher</Typography>
                              <List dense>
                                {week.questionsToAskTeacher.map((q, idx) => (
                                  <ListItem key={idx} sx={{ py: 0.5 }}>
                                    <ListItemIcon><HelpIcon sx={{ fontSize: 14, color: '#3b82f6' }} /></ListItemIcon>
                                    <ListItemText primary={q} primaryTypographyProps={{ fontSize: 11 }} />
                                  </ListItem>
                                ))}
                              </List>
                              <Button 
                                variant="contained" 
                                startIcon={<SendIcon />} 
                                onClick={() => handleAskTeacher(week.topic, week.questionsToAskTeacher)} 
                                fullWidth 
                                sx={{ mt: 2 }}
                              >
                                Ask Your Question
                              </Button>
                            </Paper>
                          </Grid>

                          {/* Mock Test */}
                          <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#dcfce7' }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><QuizIcon sx={{ fontSize: 18 }} /> Practice Test</Typography>
                              <Button 
                                variant="contained" 
                                color="success" 
                                onClick={() => handleOpenMockTest(week.topic)} 
                                fullWidth
                              >
                                Take Mock Test: {week.topic}
                              </Button>
                            </Paper>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                {/* Teacher Tips */}
                <Box sx={{ mt: 4, p: 2, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LightbulbIcon color="success" /> Teacher's Study Tips
                  </Typography>
                  <Stack spacing={1}>
                    {student.studyPlan.teacherTips.map((tip, idx) => (
                      <Typography key={idx} variant="body2" sx={{ fontSize: 12 }}>• {tip}</Typography>
                    ))}
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Performance View */}
      {selectedView === 'performance' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>🧠 Concept Mastery Analysis</Typography>
                {student.performance.conceptMastery.map((concept, idx) => (
                  <Box key={idx} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{concept.concept}</Typography>
                      <Typography variant="body2" color={concept.mastery >= 80 ? 'success.main' : 'warning.main'}>{concept.mastery}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={concept.mastery} sx={{ height: 8, borderRadius: 4 }} />
                    {concept.weakAreas.length > 0 && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        ⚠️ Weak Areas: {concept.weakAreas.join(', ')}
                      </Typography>
                    )}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info" icon={<PsychologyIcon />}>
              <Typography variant="subtitle2">📊 Performance Insight</Typography>
              <Typography variant="body2">
                Focus on <strong>{student.performance.conceptMastery.find(c => c.mastery === Math.min(...student.performance.conceptMastery.map(c => c.mastery)))?.concept}</strong>. 
                Ask your teacher for additional practice worksheets and video explanations.
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      )}

      {/* Dialogs */}
      <VideoPlayerDialog open={videoOpen} onClose={() => setVideoOpen(false)} videoTitle={currentTopic} />
      <PdfViewerDialog open={pdfOpen} onClose={() => setPdfOpen(false)} pdfTitle={currentTopic} />
      <MockTestDialog open={mockTestOpen} onClose={() => setMockTestOpen(false)} testTitle={`Mock Test: ${currentTopic}`} />
      <TeacherQuestionDialog 
        open={questionOpen} 
        onClose={() => setQuestionOpen(false)} 
        topic={currentTopic?.topic} 
        questionsToAsk={currentTopic?.questions || []} 
      />
    </Box>
  );
}